/**
 * Auswirkung einer Änderung auf die Preisautomatik je Zeitraum (`ari/ownership/impact`), je Feld
 * summiert: Tage, deren Preis bzw. Mindestnächte von der Automatik in die Hand wechseln, und Tage,
 * die der Server schon als von Hand gepflegt zählt. Immer frisch – nach einer Übertragung stimmt
 * ein zwischengespeicherter Stand nicht mehr.
 */
import { useQuery } from '@tanstack/react-query';
import { useApi, useApiProblem, type AriPushBatchRange } from '@/shared/api';

export function useOwnershipImpact(
  propertyId: string,
  ranges: readonly AriPushBatchRange[],
  enabled: boolean,
) {
  const api = useApi();
  const owning = ranges.filter((range) => range.rate != null || range.minStay != null);
  const impact = useQuery({
    queryKey: [
      'channex',
      'ariOwnershipImpact',
      propertyId,
      owning.map((range) => [range.from, range.to, range.rate, range.minStay]),
    ],
    queryFn: () =>
      Promise.all(
        owning.map((range) => api.channex.ariOwnershipImpact(propertyId, range.from, range.to)),
      ),
    enabled: enabled && owning.length > 0,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const problem = useApiProblem(impact.error);
  const items = impact.data ?? [];
  const sum = (pick: (index: number) => number) =>
    items.reduce((total, _, index) => total + pick(index), 0);
  return {
    impact,
    problem,
    owning: owning.length > 0,
    ready: owning.length === 0 || impact.isSuccess,
    /** Wartet die Abfrage ohne Netz, sagt der Dialog das statt still zu bleiben. */
    offline: impact.fetchStatus === 'paused',
    loading: owning.length > 0 && impact.isPending && impact.fetchStatus !== 'paused',
    rateDays: sum((index) => (owning[index]!.rate != null ? items[index]!.automationRateDays : 0)),
    minStayDays: sum((index) =>
      owning[index]!.minStay != null ? items[index]!.automationMinimumStayDays : 0,
    ),
    alreadyManual: sum((index) => items[index]!.alreadyManualDays),
  };
}
